import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Building2,
  DollarSign,
  Megaphone,
  Scale,
  ArrowRight,
  CheckCircle2,
  Shield,
  Users,
  Clock,
  Zap,
  ChevronRight,
  ChevronDown,
  Mail,
  Send,
  Loader2,
  Bot,
  ExternalLink,
  Package,
  CreditCard,
  Truck,
  Target,
  BarChart3,
  FileText,
  Globe,
  Phone,
  ShoppingCart,
  Briefcase,
  Check,
  Star,
  Rocket,
  ClipboardCheck,
  ThumbsUp,
  Headphones,
  Sparkles
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BLDR_URL = process.env.REACT_APP_BLDR_URL || 'https://bldr.companeze.com';

const LandingPage = () => {
  const navigate = useNavigate();
  
  // Ask a Business Question state
  const [businessQuestion, setBusinessQuestion] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [isAskingQuestion, setIsAskingQuestion] = useState(false);

  // Handle Ask Question
  const handleAskQuestion = async () => {
    if (!businessQuestion.trim()) return;
    
    setIsAskingQuestion(true);
    setAiResponse('');
    
    try {
      const response = await axios.post(`${API}/ask`, {
        question: businessQuestion
      });
      setAiResponse(response.data.answer || 'Thank you for your question. Our team will follow up with detailed guidance.');
    } catch (error) {
      setAiResponse('Thank you for your question. Please call us at (510) 422-4451 for immediate assistance.');
    } finally {
      setIsAskingQuestion(false);
    }
  };

  // Basic Package Features
  // Interview & Assessment Packages
  const interviewPackages = [
    {
      name: 'Self-Assessed Interview',
      price: '$399',
      priceType: 'one-time',
      description: 'Do-it-yourself business planning',
      features: [
        'Interactive Business Interview',
        'Complete Business Plan',
        'Industry Analysis Report',
        'Financial Projections',
        'Market Assessment',
        'Email Delivery of Documents'
      ],
      color: 'blue',
      popular: false
    },
    {
      name: 'Hosted & Managed Interview',
      price: '$599',
      priceType: 'one-time',
      description: 'Expert-guided process',
      features: [
        'Live Expert Interview Session',
        'Complete Business Plan',
        'Detailed Industry Analysis',
        'Financial Projections & Modeling',
        'Market Assessment & Strategy',
        'Document Review & Refinement',
        '30-Day Follow-up Support'
      ],
      color: 'amber',
      popular: true
    }
  ];

  // Corporate Development Package
  const corporatePackage = {
    name: 'Corporate Development',
    price: '$399',
    priceType: 'plus fees',
    description: 'Full business formation & compliance',
    features: [
      'Tax Registration & Setup',
      'DBA/Trade Name Filing',
      'Business Licensing',
      'State Compliance Requirements',
      'County & City Permits',
      'EIN/Tax ID Application',
      'Registered Agent Service',
      'Annual Report Reminders'
    ]
  };

  // Digital Packages
  const digitalPackages = [
    {
      name: 'Starter Digital',
      price: '$395',
      priceType: 'one-time',
      description: 'Get online fast',
      features: [
        'Professional Website',
        'Domain Registration',
        '1 Year Hosting Included',
        '5 Hours of Programming',
        'Mobile Responsive Design',
        'Basic SEO Setup'
      ],
      color: 'green'
    },
    {
      name: '5-Hour Monthly',
      price: '$225',
      priceType: '/month',
      description: 'Ongoing development support',
      features: [
        '5 Hours of Expert Programming',
        'Project Manager Assigned',
        'AI-Assisted Development Tools',
        'US-Based Development Team',
        'Monthly Strategy Call'
      ],
      savings: null,
      color: 'blue'
    },
    {
      name: '10-Hour Monthly',
      price: '$350',
      priceType: '/month',
      description: 'Best value for growth',
      features: [
        '10 Hours of Expert Programming',
        'Dedicated Project Manager',
        'AI-Assisted Development Tools',
        'US-Based Development Team',
        'Bi-Weekly Strategy Calls',
        'Priority Support'
      ],
      savings: '37%',
      color: 'purple',
      popular: true
    }
  ];

  // Advizzu Advisory Services - Per Minute Rates
  const advizzuSkillSets = [
    { skill: 'General Business Consulting', rate: '$2', icon: Briefcase, description: 'Business strategy, operations, planning' },
    { skill: 'Legal Guidance', rate: '$4', icon: Scale, description: 'Contract review, compliance, regulations' },
    { skill: 'Financial Advisory', rate: '$4', icon: DollarSign, description: 'Accounting, tax strategy, bookkeeping' },
    { skill: 'Marketing Strategy', rate: '$3', icon: Megaphone, description: 'Brand, campaigns, digital marketing' },
    { skill: 'Technical Consulting', rate: '$5', icon: Globe, description: 'IT, software, systems integration' },
    { skill: 'HR & Staffing', rate: '$3', icon: Users, description: 'Hiring, policies, employee relations' },
    { skill: 'Industry Specialist', rate: '$5', icon: Target, description: 'Domain-specific expert advice' },
    { skill: 'Executive Coaching', rate: '$6', icon: Star, description: 'Leadership, growth, performance' }
  ];

  // Additional Monthly Services
  const additionalServices = [
    { name: 'Bookkeeping & Tax', icon: DollarSign },
    { name: 'Legal Solutions Access', icon: Scale },
    { name: 'Marketing Manager', icon: Megaphone },
    { name: 'Sales Support Bureau', icon: Headphones },
    { name: 'Compliance Solutions', icon: Shield },
    { name: 'Broadcast Marketing', icon: Globe },
    { name: 'Escrow & Payment Systems', icon: CreditCard },
    { name: 'URL, Hosting & Management', icon: Globe }
  ];

  // SelleSystem Radials
  const selleSystemFeatures = [
    { id: 'selleship', name: 'SelleShip', desc: 'Secure shipping & escrow', icon: Truck, url: '#' },
    { id: 'sellepay', name: 'SellePay', desc: 'Payment processing', icon: CreditCard, url: '#' },
    { id: 'sellestore', name: 'SelleStore', desc: 'E-commerce solutions', icon: Package, url: '#' },
    { id: 'sellemarket', name: 'SelleMarket', desc: 'Marketplace platform', icon: ShoppingCart, url: '#' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-blue-900 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="font-bold text-blue-900 text-lg">Cz</span>
              </div>
              <div>
                <span className="text-xl font-bold text-white">Companeze</span>
                <span className="text-amber-400 text-sm ml-1">BLDR</span>
              </div>
            </div>
            
            {/* Nav Links */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#packages" className="text-blue-100 hover:text-white transition-colors text-sm font-medium">Packages</a>
              <a href="#services" className="text-blue-100 hover:text-white transition-colors text-sm font-medium">Services</a>
              <a href="#selle" className="text-blue-100 hover:text-white transition-colors text-sm font-medium">SelleSystem</a>
              <button onClick={() => navigate('/partners')} className="text-blue-100 hover:text-white transition-colors text-sm font-medium">Partners</button>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/login')} className="text-white hover:bg-white/10 text-sm">
                Login
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-blue-900 font-semibold rounded-lg text-sm transition-colors flex items-center gap-1"
                    data-testid="get-started-dropdown-btn"
                  >
                    Get Started <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white">
                  <DropdownMenuLabel className="text-blue-900 font-bold">Start Your Business</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <a 
                      href={BLDR_URL}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 cursor-pointer"
                      data-testid="dropdown-pro-options"
                    >
                      <Rocket className="w-4 h-4 text-amber-500" />
                      <span>Pro Options</span>
                      <ExternalLink className="w-3 h-3 ml-auto text-gray-400" />
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a 
                      href={BLDR_URL}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 cursor-pointer"
                      data-testid="dropdown-marketing-assessment"
                    >
                      <Megaphone className="w-4 h-4 text-purple-500" />
                      <span>Marketing Assessment</span>
                      <ExternalLink className="w-3 h-3 ml-auto text-gray-400" />
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-gray-500 text-xs font-normal">Business Analysis</DropdownMenuLabel>
                  <DropdownMenuItem 
                    onClick={() => navigate('/catalyst')}
                    className="flex items-center gap-2 cursor-pointer"
                    data-testid="dropdown-catalyst-options"
                  >
                    <Target className="w-4 h-4 text-blue-600" />
                    <span>Catalyst Options</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/interview')}
                    className="flex items-center gap-2 cursor-pointer"
                    data-testid="dropdown-lite-interview"
                  >
                    <ClipboardCheck className="w-4 h-4 text-green-500" />
                    <span>Lite Interview</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => navigate('/services')}
                    className="flex items-center gap-2 cursor-pointer"
                    data-testid="dropdown-compliance-service"
                  >
                    <Shield className="w-4 h-4 text-gray-600" />
                    <span>Compliance Service</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Main Radial */}
      <section className="pt-28 pb-16 px-6 bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <Badge className="mb-4 bg-amber-500 text-white border-0 text-sm px-4 py-1">
                <Rocket className="w-4 h-4 mr-2" />
                World's Fastest Business Development Platform
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                From Idea to Income
                <span className="block text-amber-400">in Days</span>
              </h1>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Complete business formation, compliance, marketing, and support. 
                We handle everything so you can focus on growing your business.
              </p>
              
              {/* Key Value Props */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-amber-400" />
                  <span className="text-blue-100">Entity Formation</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-amber-400" />
                  <span className="text-blue-100">Tax & Compliance</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-amber-400" />
                  <span className="text-blue-100">Banking Setup</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-amber-400" />
                  <span className="text-blue-100">24/7 Support</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/interview')}
                  className="bg-white text-blue-900 hover:bg-gray-100 text-lg px-8 py-6 font-semibold"
                >
                  Start Your Business <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.href = 'tel:5104224451'}
                  className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
                >
                  <Phone className="mr-2 w-5 h-5" /> (510) 422-4451
                </Button>
              </div>
            </div>

            {/* Right - GO FOR PRO Radial */}
            <div className="flex flex-col items-center">
              <a
                href={BLDR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-64 h-64 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 text-blue-900 shadow-2xl hover:shadow-amber-500/40 hover:scale-105 transition-all duration-300 flex flex-col items-center justify-center border-4 border-white/30 group"
                data-testid="go-for-pro-radial-btn"
              >
                <span className="text-white text-sm font-semibold uppercase tracking-wider mb-2">Push This Button</span>
                <span className="text-4xl font-black">GO FOR PRO</span>
                <span className="text-blue-900/80 text-sm mt-2">Start at $99/mo</span>
                <span className="text-xs text-blue-900/60 mt-1">+ government fees</span>
              </a>
              <p className="mt-6 text-blue-200 text-center text-sm max-w-xs">
                Join thousands of businesses launched through Companeze BLDR
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Steps to Success */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-900 text-white">Simple Process</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              3 Steps to Business Success
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Register & Apply</h3>
              <p className="text-gray-600">Sign up and tell us about your business idea</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Go Through Interview</h3>
              <p className="text-gray-600">Our AI-guided process creates your business plan</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Approve Recommendations</h3>
              <p className="text-gray-600">Review and approve your customized setup</p>
            </div>
          </div>

          {/* WE DO THE REST */}
          <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-2xl p-8 text-center text-white">
            <h3 className="text-3xl sm:text-4xl font-bold mb-4">WE DO THE REST.</h3>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Your dedicated representatives follow your requests and handle everything. 
              Focus on your business while we handle the paperwork, registrations, and compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Marketing & Selling Your Products Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-orange-500 text-white">Grow Your Revenue</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Marketing & Selling Your Products
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Building a business is just the start. We help you reach customers, market your products, 
              and generate sales with proven strategies and tools.
            </p>
          </div>

          {/* Marketing Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Digital Marketing */}
            <Card className="border-2 border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-4">
                  <Globe className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Digital Marketing</h3>
                <p className="text-gray-600 mb-4">
                  Get found online with SEO, social media, and digital advertising campaigns that drive traffic and sales.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Social Media Management</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Google & Facebook Ads</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">SEO Optimization</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* E-Commerce & Sales */}
            <Card className="border-2 border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-4">
                  <ShoppingCart className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">E-Commerce & Sales</h3>
                <p className="text-gray-600 mb-4">
                  Sell your products online with fully managed storefronts, payment processing, and order fulfillment.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Online Store Setup</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Marketplace Integration</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Payment Processing</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Content & Branding */}
            <Card className="border-2 border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-4">
                  <Megaphone className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Content & Branding</h3>
                <p className="text-gray-600 mb-4">
                  Build a strong brand identity and create compelling content that converts visitors to customers.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Brand Development</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Content Creation</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Email Marketing</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Sales Support */}
            <Card className="border-2 border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-4">
                  <Headphones className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Sales Support Bureau</h3>
                <p className="text-gray-600 mb-4">
                  Professional sales support to handle leads, customer inquiries, and close more deals.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Lead Management</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Customer Follow-up</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">24/7 Sales Support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Broadcast Marketing */}
            <Card className="border-2 border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-4">
                  <Mail className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Broadcast Marketing</h3>
                <p className="text-gray-600 mb-4">
                  Reach thousands of potential customers with targeted email, SMS, and promotional campaigns.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Email Campaigns</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">SMS Marketing</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Promotional Campaigns</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Analytics & Optimization */}
            <Card className="border-2 border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-4">
                  <BarChart3 className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Analytics & ROI</h3>
                <p className="text-gray-600 mb-4">
                  Track what's working and optimize your marketing spend for maximum return on investment.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Performance Tracking</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">ROI Analysis</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-700">Growth Recommendations</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Marketing CTA */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Ready to Grow Your Sales?</h3>
            <p className="text-orange-100 mb-6 max-w-2xl mx-auto">
              Our marketing solutions are tailored to your industry and budget. 
              From $599/month, get a complete marketing plan to promote your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-white text-orange-600 hover:bg-gray-100 font-semibold"
                onClick={() => window.open(BLDR_URL, '_blank')}
              >
                Get Marketing Plan
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10"
                onClick={() => navigate('/services')}
              >
                View All Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 24/7 Sales & Support Bureau Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <Badge className="mb-4 bg-green-500 text-white border-0">
                <Headphones className="w-3 h-3 mr-1" />
                24/7 US-Based
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Get Your 24/7 Sales & Support Bureau
              </h2>
              <p className="text-xl text-blue-100 mb-6">
                Get notified your way. Never miss that sale or support message again.
              </p>
              
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 mb-6">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-4xl font-bold text-amber-400">$99</span>
                  <span className="text-blue-200">/month</span>
                  <span className="text-blue-300">+</span>
                  <span className="text-2xl font-bold text-amber-400">$1.55</span>
                  <span className="text-blue-200">/minute</span>
                </div>
                <p className="text-blue-200 text-sm">Professional US-based representatives</p>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span className="text-blue-100">24/7 Live Answering Service</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span className="text-blue-100">US-Based Professional Representatives</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span className="text-blue-100">Instant Notifications (Call, Text, Email)</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span className="text-blue-100">Lead Capture & Qualification</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span className="text-blue-100">Use as Your VIP Planner</span>
                </li>
              </ul>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  className="bg-green-500 hover:bg-green-600 text-white font-semibold"
                  onClick={() => window.open(BLDR_URL, '_blank')}
                >
                  Get Started - $99/mo
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => window.location.href = 'tel:5104224451'}
                >
                  <Phone className="w-5 h-5 mr-2" /> Call to Learn More
                </Button>
              </div>
            </div>

            {/* Right - Feature Highlights */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center">
                <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-white mb-1">Never Miss a Call</h3>
                <p className="text-blue-200 text-sm">Every call answered professionally</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center">
                <div className="w-14 h-14 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-white mb-1">Instant Alerts</h3>
                <p className="text-blue-200 text-sm">Get notified your way</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center">
                <div className="w-14 h-14 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-white mb-1">US-Based Team</h3>
                <p className="text-blue-200 text-sm">Professional representatives</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center">
                <div className="w-14 h-14 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-white mb-1">24/7 Coverage</h3>
                <p className="text-blue-200 text-sm">Round the clock support</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW PRICING STRUCTURE */}
      <section id="packages" className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-500 text-white">Pick Your Package</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Business Development Packages
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From interview to income - choose the package that fits your needs
            </p>
          </div>

          {/* Interview & Assessment Packages */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              <FileText className="inline w-6 h-6 mr-2 text-blue-600" />
              Interview & Business Plan Packages
            </h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {interviewPackages.map((pkg, idx) => (
                <Card key={idx} className={`border-2 ${pkg.popular ? 'border-amber-500' : 'border-gray-200'} bg-white relative overflow-hidden`}>
                  {pkg.popular && (
                    <>
                      <div className="absolute top-0 left-0 right-0 h-1.5 bg-amber-500"></div>
                      <Badge className="absolute top-2 right-2 bg-amber-500 text-white text-xs border-0">Popular</Badge>
                    </>
                  )}
                  <CardContent className={`p-6 ${pkg.popular ? 'pt-8' : ''}`}>
                    <h4 className="text-xl font-bold text-gray-900">{pkg.name}</h4>
                    <div className="mt-2 mb-2">
                      <span className="text-3xl font-bold text-blue-900">{pkg.price}</span>
                      <span className="text-gray-500 text-sm ml-1">{pkg.priceType}</span>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>
                    <ul className="space-y-2 mb-6 text-sm">
                      {pkg.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2">
                          <Check className={`w-4 h-4 ${pkg.popular ? 'text-amber-500' : 'text-green-500'} flex-shrink-0 mt-0.5`} />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className={`w-full ${pkg.popular ? 'bg-amber-500 hover:bg-amber-600 text-blue-900' : 'bg-blue-900 hover:bg-blue-800 text-white'} font-semibold`}
                      onClick={() => window.open(BLDR_URL, '_blank')}
                    >
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Corporate Development Package */}
          <div className="mb-16">
            <Card className="border-2 border-green-500 bg-white max-w-2xl mx-auto">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-green-500"></div>
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">{corporatePackage.name}</h4>
                    <div>
                      <span className="text-2xl font-bold text-blue-900">{corporatePackage.price}</span>
                      <span className="text-gray-500 text-sm ml-1">{corporatePackage.priceType}</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 mb-4">{corporatePackage.description}</p>
                <div className="grid sm:grid-cols-2 gap-2 mb-6">
                  {corporatePackage.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                <Button 
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold"
                  onClick={() => window.open(BLDR_URL, '_blank')}
                >
                  Start Corporate Setup
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Digital Packages */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
              <Globe className="inline w-6 h-6 mr-2 text-purple-600" />
              Digital Packages
            </h3>
            <p className="text-center text-gray-600 mb-6 max-w-2xl mx-auto">
              HYBRID tools with a Project Manager, Expert Programmer, AI-assisted development & US presence.
              <span className="block mt-1 font-semibold text-purple-600">Each package saves 50% or more!</span>
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {digitalPackages.map((pkg, idx) => (
                <Card key={idx} className={`border-2 ${pkg.popular ? 'border-purple-500' : 'border-gray-200'} bg-white relative overflow-hidden`}>
                  {pkg.popular && (
                    <>
                      <div className="absolute top-0 left-0 right-0 h-1.5 bg-purple-500"></div>
                      <Badge className="absolute top-2 right-2 bg-purple-500 text-white text-xs border-0">Best Value</Badge>
                    </>
                  )}
                  {pkg.savings && (
                    <Badge className="absolute top-2 left-2 bg-green-500 text-white text-xs border-0">Save {pkg.savings}</Badge>
                  )}
                  <CardContent className={`p-6 ${pkg.popular ? 'pt-8' : ''}`}>
                    <h4 className="text-xl font-bold text-gray-900">{pkg.name}</h4>
                    <div className="mt-2 mb-2">
                      <span className="text-3xl font-bold text-blue-900">{pkg.price}</span>
                      <span className="text-gray-500 text-sm ml-1">{pkg.priceType}</span>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>
                    <ul className="space-y-2 mb-6 text-sm">
                      {pkg.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2">
                          <Check className={`w-4 h-4 ${pkg.popular ? 'text-purple-500' : 'text-green-500'} flex-shrink-0 mt-0.5`} />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className={`w-full ${pkg.popular ? 'bg-purple-500 hover:bg-purple-600' : 'bg-blue-900 hover:bg-blue-800'} text-white font-semibold`}
                      onClick={() => window.open(BLDR_URL, '_blank')}
                    >
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Advizzu Advisory Services - Per Minute */}
      <section className="py-20 px-6 bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-500 text-white border-0">
              <Phone className="w-3 h-3 mr-1" />
              We're Available On Call
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Advizzu Expert Advisory Lines
            </h2>
            <p className="text-lg text-purple-200 max-w-2xl mx-auto">
              Get expert advice when you need it. Pay only for the time you use.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-xl px-6 py-3">
              <span className="text-purple-200">Rates from</span>
              <span className="text-3xl font-bold text-amber-400">$2</span>
              <span className="text-purple-200">to</span>
              <span className="text-3xl font-bold text-amber-400">$6</span>
              <span className="text-purple-200">/minute</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {advizzuSkillSets.map((item, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur rounded-xl p-5 hover:bg-white/20 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="text-xl font-bold text-amber-400">{item.rate}</span>
                    <span className="text-purple-200 text-sm">/min</span>
                  </div>
                </div>
                <h4 className="font-semibold text-white mb-1">{item.skill}</h4>
                <p className="text-purple-200 text-sm">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="bg-white/10 backdrop-blur rounded-2xl p-8 max-w-2xl mx-auto">
              <h3 className="text-xl font-bold text-white mb-4">Ready to Talk to an Expert?</h3>
              <p className="text-purple-200 mb-6">
                Call our advisory line now. No appointment needed.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  className="bg-amber-500 hover:bg-amber-600 text-blue-900 font-semibold"
                  onClick={() => window.location.href = 'tel:5104224451'}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call (510) 422-4451
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => navigate('/services')}
                >
                  View All Services
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ongoing Services & Analytics for Existing Businesses */}
      <section id="services" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-600 text-white">For Existing Businesses</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Ongoing Services & Analytics
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Already have a business? We provide ongoing support, analytics, and growth services to help you scale.
            </p>
          </div>

          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Ongoing Services */}
            <Card className="border-2 border-gray-200">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-900 rounded-xl flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Ongoing Services</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Monthly support services to keep your business running smoothly
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700">Bookkeeping & Tax Preparation</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700">Legal Solutions Access</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700">Compliance Monitoring</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700">Marketing Management</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700">Sales Support Bureau</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700">URL, Hosting & Management</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700">Escrow & Payment Systems</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Analytics & Reporting */}
            <Card className="border-2 border-gray-200">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Analytics & Reporting</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Data-driven insights to optimize your business performance
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-purple-500" />
                    <span className="text-gray-700">Business Performance Dashboard</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-purple-500" />
                    <span className="text-gray-700">Revenue & Expense Tracking</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-purple-500" />
                    <span className="text-gray-700">Marketing ROI Analysis</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-purple-500" />
                    <span className="text-gray-700">Customer Acquisition Metrics</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-purple-500" />
                    <span className="text-gray-700">Sales Pipeline Reports</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-purple-500" />
                    <span className="text-gray-700">Compliance Status Tracking</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-purple-500" />
                    <span className="text-gray-700">Growth Recommendations</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* CTA for Existing Businesses */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Already Have a Business?</h3>
            <p className="text-green-100 mb-6 max-w-2xl mx-auto">
              Get a free consultation to see how our ongoing services and analytics can help you grow. 
              No commitment required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-white text-green-700 hover:bg-gray-100 font-semibold"
                onClick={() => navigate('/catalyst')}
              >
                Get Business Analysis
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10"
                onClick={() => window.location.href = 'tel:5104224451'}
              >
                <Phone className="w-5 h-5 mr-2" /> Schedule Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Ask a Question */}
      <section className="py-16 px-6 bg-blue-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-green-500 text-white">Free</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Ask a Business Question
            </h2>
            <p className="text-gray-600">Get instant answers from our AI advisor</p>
          </div>

          <Card className="border-2 border-blue-200">
            <CardContent className="p-6">
              <div className="flex gap-2">
                <Input
                  placeholder="e.g., How do I form an LLC in California?"
                  value={businessQuestion}
                  onChange={(e) => setBusinessQuestion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAskQuestion()}
                  className="flex-1 bg-white border-blue-200 focus:border-blue-900"
                  data-testid="ask-question-input"
                />
                <Button
                  onClick={handleAskQuestion}
                  disabled={isAskingQuestion || !businessQuestion.trim()}
                  className="bg-blue-900 hover:bg-blue-800 text-white px-6"
                  data-testid="ask-question-btn"
                >
                  {isAskingQuestion ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              
              {aiResponse && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-blue-100">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-900 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-blue-900 mb-1">Advizzu Response</p>
                      <p className="text-gray-700 text-sm whitespace-pre-wrap">{aiResponse}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Catalyst Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-900 to-blue-800 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-amber-500 text-white border-0">
                <Target className="w-3 h-3 mr-1" />
                Business Analysis
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Catalyst Business Optimization
              </h2>
              <p className="text-xl text-blue-100 mb-6">
                Get a complete analysis of your business across 9 key dimensions. 
                Identify what to fix before you scale.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/catalyst')}
                  className="bg-amber-500 hover:bg-amber-600 text-blue-900 font-semibold"
                >
                  Full Analysis - $199 <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/catalyst')}
                  className="border-white text-white hover:bg-white/10"
                >
                  Single Problem - $49
                </Button>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-2xl p-8 border border-white/20">
              <h3 className="text-lg font-semibold mb-4">What You'll Receive:</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-amber-400" />
                  <span>CBIS Score (9-dimension analysis)</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-amber-400" />
                  <span>Critical Issues & Priority Fixes</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-amber-400" />
                  <span>90-Day Action Sprint</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-amber-400" />
                  <span>12-Month Strategic Roadmap</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SelleSystem Radials */}
      <section id="selle" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-600 text-white">SelleSystem</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Integrated Business Solutions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Expand your business with our ecosystem of integrated tools
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {selleSystemFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <a
                  key={feature.id}
                  href={feature.url}
                  className="group flex flex-col items-center text-center p-6 bg-gray-50 rounded-2xl border-2 border-gray-200 hover:border-purple-400 hover:shadow-xl transition-all"
                  data-testid={`selle-${feature.id}-btn`}
                >
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">{feature.name}</h3>
                  <p className="text-sm text-gray-500">{feature.desc}</p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Start Your Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of entrepreneurs who launched their businesses with Companeze.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={BLDR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 bg-amber-500 hover:bg-amber-600 text-blue-900 font-semibold text-lg rounded-lg transition-colors"
            >
              Get Started - $99/mo <ArrowRight className="ml-2 w-5 h-5" />
            </a>
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 text-lg px-8 py-4"
              onClick={() => window.location.href = 'tel:5104224451'}
            >
              Call (510) 422-4451
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <span className="font-bold text-blue-900">Cz</span>
                </div>
                <div>
                  <span className="text-lg font-bold text-white">Companeze</span>
                  <span className="text-amber-400 text-sm ml-1">BLDR</span>
                </div>
              </div>
              <p className="text-sm">World's fastest business development platform.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Solutions</h4>
              <ul className="space-y-2 text-sm">
                <li><a href={BLDR_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Basic Package</a></li>
                <li><a href={BLDR_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Pro Package</a></li>
                <li><button onClick={() => navigate('/catalyst')} className="hover:text-white transition-colors">Catalyst Analysis</button></li>
                <li><button onClick={() => navigate('/services')} className="hover:text-white transition-colors">Advizzu Services</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/partners')} className="hover:text-white transition-colors">Partners</button></li>
                <li><button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Client Login</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="tel:5104224451" className="hover:text-white transition-colors">(510) 422-4451</a></li>
                <li><a href="mailto:info@companeze.com" className="hover:text-white transition-colors">info@companeze.com</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>© 2025 Companeze BLDR. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
